/**
 * AccountHeader — slim header rendered by <PlatformHeader/> whenever
 * the visitor is on any `/account/*` route.
 *
 * Contract (matches user requirement, iter 268):
 *   • NO module Menu (hamburger nav is gone on account pages).
 *   • NO Book-a-Table CTA.
 *   • NO cart, NO nav dropdowns, NO primary nav.
 *   • Provides a "← Back to [Module]" button — falls back to the PWA
 *     launcher when no same-origin referrer is available.
 *   • Keeps LanguagePicker + ThemeToggle for parity with the rest of
 *     the suite.
 *   • Renders the Vendor↔Member context switcher for users that have
 *     both profiles.
 *
 * The Emergency / Modules / Account chrome from GoogleTranslateBar
 * already lives ABOVE this header — we intentionally don't
 * re-render any of that here.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useMeState } from "@/hooks/useMeState";
import LanguagePicker from "@/components/LanguagePicker";
import ThemeToggle from "@/components/ThemeToggle";
import AccountContextSwitcher from "@/components/AccountContextSwitcher";
import AppVersionPill from "@/components/AppVersionPill";
import Tip from "@/components/Tip";
import { getModuleHost } from "@/lib/moduleHost";

const REF_KEY = "hp_account_return_ref";

/** Resolve where "Back" should send the user.
 *
 * Preference order:
 *   1. sessionStorage.hp_account_return_ref  (set on entering /account)
 *   2. same-origin document.referrer          (deep-link fallback)
 *   3. current module host home               (if we're on app.<module>)
 *   4. https://pwa.hustlesuite.au             (universal fallback)
 */
function resolveReturnTarget() {
  if (typeof window === "undefined") {
    return { label: "Launcher", href: "https://pwa.hustlesuite.au" };
  }
  // 1. Stored ref (set right before navigating into /account).
  try {
    const raw = sessionStorage.getItem(REF_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.href && parsed.label) return parsed;
    }
  } catch (_e) { /* ignore */ }

  // 2. Same-origin referrer (never point back at /account itself).
  try {
    if (document.referrer) {
      const u = new URL(document.referrer);
      if (u.hostname === window.location.hostname
          && !u.pathname.startsWith("/account")) {
        return { label: "Back", href: u.pathname + u.search };
      }
    }
  } catch (_e) { /* ignore */ }

  // 3. Module host home.
  const m = getModuleHost();
  if (m?.title && m?.home) {
    return { label: m.title, href: m.home };
  }

  // 4. Universal fallback.
  return { label: "Launcher", href: "https://pwa.hustlesuite.au" };
}

export default function AccountHeader() {
  const location = useLocation();
  const { user, checking } = useAuth();
  const { state, setActive } = useMeState();
  const [ret, setRet] = useState(() => resolveReturnTarget());

  // Re-resolve every time we navigate WITHIN /account/* (title stays
  // stable but path may shift, e.g. /account → /account/credits).
  useEffect(() => { setRet(resolveReturnTarget()); }, [location.pathname]);

  const initial = useMemo(
    () => (user?.name || user?.email || "?").trim().charAt(0).toUpperCase(),
    [user]
  );

  const backIsExternal = /^https?:\/\//i.test(ret.href);

  return (
    <header
      data-testid="account-header"
      style={{ marginTop: "var(--global-bar-h)" }}
      className="sticky top-0 z-40 bg-[#FDFBF7]/90 dark:bg-stone-900/90 backdrop-blur-xl border-b border-[#E8E5DF] dark:border-stone-700"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-3 h-16 md:h-20">
        {/* Left: Back-to-module */}
        <Tip label={`Return to ${ret.label}`}>
          {backIsExternal ? (
            <a
              href={ret.href}
              data-testid="account-back-to-module"
              className="inline-flex items-center gap-2 rounded-full border border-[#E8E5DF] dark:border-stone-700 bg-white dark:bg-stone-800 hover:bg-[#F3F0EA] dark:hover:bg-stone-700 text-[#121614] dark:text-stone-100 px-3 h-10 text-sm font-medium transition-colors"
            >
              <ArrowLeft className="h-4 w-4" strokeWidth={2} />
              <span className="hidden sm:inline max-w-[16ch] truncate">{ret.label}</span>
              <span className="sm:hidden">Back</span>
            </a>
          ) : (
            <Link
              to={ret.href}
              data-testid="account-back-to-module"
              className="inline-flex items-center gap-2 rounded-full border border-[#E8E5DF] dark:border-stone-700 bg-white dark:bg-stone-800 hover:bg-[#F3F0EA] dark:hover:bg-stone-700 text-[#121614] dark:text-stone-100 px-3 h-10 text-sm font-medium transition-colors"
            >
              <ArrowLeft className="h-4 w-4" strokeWidth={2} />
              <span className="hidden sm:inline max-w-[16ch] truncate">{ret.label}</span>
              <span className="sm:hidden">Back</span>
            </Link>
          )}
        </Tip>

        {/* Centre: Account brand */}
        <Link
          to="/account"
          data-testid="account-header-brand"
          className="flex-1 min-w-0 flex items-center gap-2 text-center sm:text-left"
          aria-label="My Account"
        >
          <span
            className="font-serif-display text-lg md:text-2xl font-medium text-[#121614] dark:text-stone-100 truncate"
            data-testid="account-header-title"
          >
            {state?.active_context === "business" ? "Business · " : ""}
            My Account
          </span>
        </Link>

        {/* Right: switcher (if applicable) + lang/theme + avatar */}
        <div className="flex items-center gap-2 shrink-0">
          {state && (
            <AccountContextSwitcher
              state={state}
              onChange={setActive}
              compact
            />
          )}
          <div className="hidden md:flex items-center gap-2">
            <LanguagePicker />
            <ThemeToggle />
          </div>
          {!checking && user && (
            <Tip label="Signed in">
              <span
                data-testid="account-header-avatar"
                className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#5A7247] text-white font-semibold"
              >
                {initial || <User className="h-4 w-4" strokeWidth={2} />}
              </span>
            </Tip>
          )}
        </div>
      </div>
      {/* iter316q (2026-02-23) — bundle hash badge, fixed bottom-right.
          Lets support instantly ask a vendor whether they're on the
          latest deploy when they report "UI vanished". */}
      <AppVersionPill />
    </header>
  );
}

/** Utility: call this JUST BEFORE navigating INTO `/account/*` from
 *  a module link so the "Back" button can name the exact origin.
 *
 *  ```js
 *  import { rememberAccountReturn } from "@/components/AccountHeader";
 *  rememberAccountReturn({ label: "HustleEats", href: "/menu" });
 *  ```
 */
export function rememberAccountReturn(ref) {
  if (typeof window === "undefined" || !ref?.href || !ref?.label) return;
  try {
    sessionStorage.setItem(REF_KEY, JSON.stringify({
      label: String(ref.label).slice(0, 40),
      href:  String(ref.href).slice(0, 500),
    }));
  } catch (_e) { /* ignore */ }
}
